import { useState, useEffect } from 'react';
import { MessageSquare, Eye, X, Trash2 } from 'lucide-react';
import { useToast } from '../components/Toast';
import api from '../Server';

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;

  const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
  const ist  = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata', hour12: true });
  const us   = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York', hour12: true });

  return { date, ist, us };
}

function MessageModal({ enquiry, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Message from {enquiry.name}</h3>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">From</label>
            <div style={{ fontWeight: 600 }}>
              {enquiry.name} — <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>{enquiry.email}</span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Subject</label>
            <div style={{ fontWeight: 600 }}>{enquiry.subject}</div>
          </div>
          <div className="form-group">
            <label className="form-label">Message</label>
            <div style={{
              fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.8,
              background: 'var(--border-light)', padding: '14px 16px', borderRadius: 8
            }}>
              {enquiry.description || enquiry.message}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Received At</label>
            <div style={{ fontSize: 13 }}>
              {(() => {
                const raw = enquiry.createdAt || enquiry.created_at || enquiry.createdDate || enquiry.date;
                const f = formatDate(raw);
                return f ? (
                  <>
                    <div style={{ fontWeight: 600 }}>{f.date}</div>
                    <div style={{ fontSize: 11, color: '#3B82F6', marginTop: 3 }}>🇮🇳 IST &nbsp;{f.ist}</div>
                    <div style={{ fontSize: 11, color: '#8B5CF6', marginTop: 2 }}>🇺🇸 EST &nbsp;{f.us}</div>
                  </>
                ) : '—';
              })()}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function Enquiries() {
  const toast = useToast();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewEnq, setViewEnq] = useState(null);

  // ── GET /message/getMessages ──────────────────────────────────────────────
  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const res = await api.get('/message/getMessages');

      // API may return array directly, or wrapped: { data: [] } / { messages: [] } / { result: [] }
      const raw = res.data;
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.messages)
            ? raw.messages
            : Array.isArray(raw?.result)
              ? raw.result
              : [];

      console.log('[Enquiries] API response:', raw);   // debug — remove after confirming shape
      setEnquiries([...list].reverse());
    } catch (err) {
      console.error('[Enquiries] fetch error:', err);
      toast.error('Load Failed', 'Failed to load enquiries. Please try again.');
      setEnquiries([]); // always keep state as array so .filter() never throws
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  // ── POST /message/deleteMessage ───────────────────────────────────────────
  const handleDelete = async (enq) => {
    const ok = await toast.confirm(`This will permanently delete the enquiry from "${enq.name}".`, { confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      await api.post('/message/deleteMessage', { id: enq.id });
      setEnquiries(prev => prev.filter(e => e.id !== enq.id));
      toast.success('Deleted!', `Enquiry from "${enq.name}" has been removed.`);
    } catch (err) {
      toast.error('Delete Failed', 'Failed to delete enquiry. Please try again.');
    }
  };

  const safeList = Array.isArray(enquiries) ? enquiries : [];

  return (
    <div className="page-content">
      <div className="page-header">
        <h2>Enquiries</h2>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(1, 1fr)', maxWidth: 200 }}>
        <div className="stat-card" style={{ '--card-accent': '#3B82F6', '--card-icon-bg': 'rgba(59,130,246,0.12)' }}>
          <div className="stat-card-header">
            <span className="stat-label">Total Enquiries</span>
            <div className="stat-icon"><MessageSquare size={18} /></div>
          </div>
          <div className="stat-value">{safeList.length}</div>
        </div>
      </div>

      {/* Table */}
      <div className="table-card">
        <div className="table-card-header">
          <h3>All Enquiries ({safeList.length})</h3>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Email ID</th>
                <th>Subject</th>
                <th>Message</th>
                <th>Action</th>
                <th>Created Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state"><p>Loading enquiries...</p></div>
                  </td>
                </tr>
              ) : safeList.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state"><MessageSquare size={40} /><p>No enquiries yet.</p></div>
                  </td>
                </tr>
              ) : safeList.map((enq, i) => (
                <tr key={enq.id}>
                  <td><strong>{i + 1}</strong></td>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{enq.name}</td>
                  <td style={{ fontSize: 12.5 }}>{enq.email}</td>
                  <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {enq.subject}
                  </td>
                  <td style={{ fontSize: 12.5, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {enq.description || enq.message}
                  </td>
                  <td>
                    <div className="action-btns">
                      <button className="action-btn view" onClick={() => setViewEnq(enq)} title="View Message">
                        <Eye size={15} />
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => handleDelete(enq)}
                        title="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                  <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                    {(() => {
                      const raw = enq.createdAt || enq.created_at || enq.createdDate || enq.date;
                      const f = formatDate(raw);
                      return f ? (
                        <>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3 }}>{f.date}</div>
                          <div style={{ fontSize: 11, color: '#3B82F6' }}>🇮🇳 IST &nbsp;{f.ist}</div>
                          <div style={{ fontSize: 11, color: '#8B5CF6', marginTop: 2 }}>🇺🇸 EST &nbsp;{f.us}</div>
                        </>
                      ) : '—';
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {viewEnq && <MessageModal enquiry={viewEnq} onClose={() => setViewEnq(null)} />}
    </div>
  );
}
